import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { generateRequestId } from '_services/queue-status-service';
import useQueueStatus from '../../../hooks/useQueueStatus';
import { fetchECACReport, resetECACReport, selectECACReportData } from 'redux/slices/sla-reports/ecac-report';
import { fetchExternalCallPointReport, resetExternalCallPointReport, selectExternalCallPointReportData } from 'redux/slices/sla-reports/external-call-point-report';
import { fetchBoardingSla, resetBoardingSla, selectBoardingSlaData } from 'redux/slices/sla-reports/boarding-sla';
import { fetchTransferSla, resetTransferSla, selectTransferSlaData } from 'redux/slices/sla-reports/transfer-sla';
import { fetchInboundSla, resetInboundSla, selectInboundSlaData } from 'redux/slices/sla-reports/inbound-sla';
import { fetchTerminalReceptionReport, resetTerminalReception, selectTerminalReceptionData } from 'redux/slices/sla-reports/terminal-reception';
import TerminalReceptionSummary, { getOverallKpis as getTerminalOverallKpis } from './terminal-reception-summary';
import BoardingSlaSummary, { getOverallKpis as getBoardingOverallKpis } from './boarding-sla-summary';
import TransferSlaSummary, { buildOverallKpis as getTransferOverallKpis } from './transfer-sla-summary';
import InboundSlaSummary, { getOverallKpis as getInboundOverallKpis } from './inbound-sla-summary';
import { exportSlaSummaryExcel } from './sla-summary-excel';
import SlaReasonSummaryPanel, { mergeReasonSummaries, reasonSummaryFromReports } from './sla-reason-summary';

// Summary view only renders KPI scoreboards, never the per-passenger rawData
// tables, so we ask the backend to drop rawData via hideBreakdown=true. That
// shrinks each response by 10-100x (the slow part for big date ranges) and
// avoids freezing React while it accepts the giant payloads into Redux.
const SUB_REPORTS = [
    {
        key: 'ecac',
        label: 'Terminal Reception',
        thunk: fetchECACReport,
        reset: resetECACReport,
        buildParams: (base) => ({ ...base, format: 'kpi', hideBreakdown: true }),
    },
    {
        key: 'external-call-point',
        label: 'External Call Point',
        thunk: fetchExternalCallPointReport,
        reset: resetExternalCallPointReport,
        buildParams: (base) => ({ ...base, hideBreakdown: true }),
    },
    {
        key: 'boarding-sla',
        label: 'Boardings',
        thunk: fetchBoardingSla,
        reset: resetBoardingSla,
        buildParams: (base) => ({ ...base, hideBreakdown: true }),
    },
    {
        key: 'transfer-sla',
        label: 'Transfer SLA',
        thunk: fetchTransferSla,
        reset: resetTransferSla,
        buildParams: (base) => ({ ...base, hideBreakdown: true }),
    },
    {
        key: 'inbound-sla',
        label: 'Inbounds',
        thunk: fetchInboundSla,
        reset: resetInboundSla,
        buildParams: (base) => ({ ...base, hideBreakdown: true }),
    },
    {
        key: 'terminal-reception',
        label: 'Terminal Holding Points (218/407)',
        thunk: fetchTerminalReceptionReport,
        reset: resetTerminalReception,
        buildParams: (base) => ({ ...base, hideBreakdown: true }),
    },
];

const MAX_DAYS = 32;

// Maps each section to the SAME overall-KPI computation its summary component
// uses when rendering, so the Excel export matches the on-screen numbers 1:1.
// hideMinutes mirrors Transfer SLA, which renders an en-dash for Minutes.
const SECTION_EXPORT_CONFIG = {
    'ecac': { compute: (d, s) => getTerminalOverallKpis(d, s, false), hideMinutes: false },
    'external-call-point': { compute: (d, s) => getTerminalOverallKpis(d, s, false), hideMinutes: false },
    'boarding-sla': { compute: (d, s) => getBoardingOverallKpis(d, s, false), hideMinutes: false },
    'transfer-sla': { compute: (d, s) => getTransferOverallKpis(d, s), hideMinutes: true },
    'inbound-sla': { compute: (d, s) => getInboundOverallKpis(d, s, false), hideMinutes: false },
    'terminal-reception': { compute: (d, s) => getTerminalOverallKpis(d, s, false), hideMinutes: false },
};

const toLocalDateString = (d) => {
    const dt = new Date(d);
    return `${dt.getFullYear()}-${String(dt.getMonth() + 1).padStart(2, '0')}-${String(dt.getDate()).padStart(2, '0')}`;
};

const SlaSummaryReport = ({ dateRange, iata, runReportRef, onRunLoadingChange }) => {
    const dispatch = useDispatch();
    const cancelledRef = useRef(false);

    const [currentIndex, setCurrentIndex] = useState(null);
    const [errors, setErrors] = useState({});
    const [done, setDone] = useState(false);
    const [completedKeys, setCompletedKeys] = useState({});
    const [exporting, setExporting] = useState(false);
    const [includeReasonSummary, setIncludeReasonSummary] = useState(false);
    const [activeRequestId, setActiveRequestId] = useState(null);
    const { queueStatus, position } = useQueueStatus(activeRequestId);

    const ecacData = useSelector(selectECACReportData);
    const extCallPointData = useSelector(selectExternalCallPointReportData);
    const boardingData = useSelector(selectBoardingSlaData);
    const transferData = useSelector(selectTransferSlaData);
    const inboundData = useSelector(selectInboundSlaData);
    const termReceptionData = useSelector(selectTerminalReceptionData);

    const dataByKey = {
        'ecac': ecacData,
        'external-call-point': extCallPointData,
        'boarding-sla': boardingData,
        'transfer-sla': transferData,
        'inbound-sla': inboundData,
        'terminal-reception': termReceptionData,
    };

    const isRunning = currentIndex !== null;

    const handleRunReport = useCallback(async () => {
        const startDate = dateRange?.[0];
        const endDate = dateRange?.[1];
        if (!startDate || !endDate || !iata) return;

        const diffMs = new Date(endDate) - new Date(startDate);
        const diffDays = diffMs / (1000 * 60 * 60 * 24);
        if (diffDays > MAX_DAYS) {
            alert(`The SLA Summary Report can only be run for up to ${MAX_DAYS} days at a time. Please select a shorter date range.`);
            return;
        }

        const formattedStartDate = toLocalDateString(startDate);
        const formattedEndDate = toLocalDateString(endDate);
        const baseParams = { startDate: formattedStartDate, endDate: formattedEndDate, iata };

        cancelledRef.current = false;
        setErrors({});
        setDone(false);
        setCompletedKeys({});

        // Run sub-reports one after another to keep load on the cpu service
        // (and its downstream flights / Firestore / PRM API dependencies)
        // smooth. With hideBreakdown=true each response is tiny, so the
        // sequential wall time is acceptable.
        for (let i = 0; i < SUB_REPORTS.length; i++) {
            if (cancelledRef.current) return;
            setCurrentIndex(i);
            const report = SUB_REPORTS[i];
            const reqId = generateRequestId();
            setActiveRequestId(reqId);
            try {
                await dispatch(report.thunk(report.buildParams({ ...baseParams, requestId: reqId }))).unwrap();
                setCompletedKeys((prev) => ({ ...prev, [report.key]: true }));
            } catch (e) {
                setErrors((prev) => ({ ...prev, [report.key]: e?.message ?? 'Failed' }));
                setCompletedKeys((prev) => ({ ...prev, [report.key]: true }));
            }
        }

        setActiveRequestId(null);
        if (!cancelledRef.current) {
            setCurrentIndex(null);
            setDone(true);
        }
    }, [dispatch, dateRange, iata]);

    useEffect(() => {
        if (runReportRef) runReportRef.current = handleRunReport;
        return () => { if (runReportRef) runReportRef.current = null; };
    }, [handleRunReport, runReportRef]);

    useEffect(() => {
        if (onRunLoadingChange) onRunLoadingChange(isRunning);
    }, [isRunning, onRunLoadingChange]);

    useEffect(() => {
        return () => {
            cancelledRef.current = true;
            setActiveRequestId(null);
            SUB_REPORTS.forEach((r) => dispatch(r.reset()));
        };
    }, [dispatch]);

    const renderSummary = (key, data) => {
        if (!data) return null;
        const dailyReports = data.dailyReports ?? [];

        switch (key) {
            case 'ecac':
            case 'external-call-point':
            case 'terminal-reception':
                return (
                    <TerminalReceptionSummary
                        dailyReports={dailyReports}
                        overallSummary={data.summary}
                    />
                );
            case 'boarding-sla':
                return (
                    <BoardingSlaSummary
                        dailyReports={dailyReports}
                        overallSummary={data.summary}
                        includeNoData={false}
                        hideDailyBreakdown={true}
                    />
                );
            case 'transfer-sla':
                return (
                    <TransferSlaSummary
                        dailyReports={dailyReports}
                        overallSummary={data.summary}
                        hideDailyBreakdown={true}
                    />
                );
            case 'inbound-sla':
                return (
                    <InboundSlaSummary
                        dailyReports={dailyReports}
                        overallSummary={data.summary}
                        includeNoData={false}
                        hideDailyBreakdown={true}
                    />
                );
            default:
                return null;
        }
    };

    // Build the export payload from the same overall-KPI computations the
    // summary components render, so the downloaded .xlsx matches the screen.
    const exportSections = useMemo(() => {
        if (!done) return [];
        return SUB_REPORTS.map((report) => {
            const data = dataByKey[report.key];
            const cfg = SECTION_EXPORT_CONFIG[report.key];
            if (!data || errors[report.key] || !cfg) return null;
            const kpis = cfg.compute(data.dailyReports ?? [], data.summary);
            const rows = kpis ? Object.values(kpis).filter(Boolean) : [];
            if (!rows.length) return null;
            return { label: report.label, hideMinutes: cfg.hideMinutes, rows };
        }).filter(Boolean);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [done, errors, ecacData, extCallPointData, boardingData, transferData, inboundData, termReceptionData]);

    const handleExport = useCallback(async () => {
        if (!exportSections.length) return;
        setExporting(true);
        try {
            await exportSlaSummaryExcel({
                sections: exportSections,
                iata,
                startDate: toLocalDateString(dateRange?.[0]),
                endDate: toLocalDateString(dateRange?.[1]),
            });
        } catch (e) {
            console.error('SLA Summary Excel export failed:', e);
            alert('Failed to generate the Excel file. Please try again.');
        } finally {
            setExporting(false);
        }
    }, [exportSections, iata, dateRange]);

    const progressPercent = isRunning
        ? Math.round(((currentIndex + 1) / SUB_REPORTS.length) * 100)
        : done ? 100 : 0;

    return (
        <div>
            {/* Progress card while running */}
            {(isRunning || (!done && progressPercent === 0)) && (
                <div style={{
                    border: '1px solid #e5e7eb',
                    borderRadius: '8px',
                    padding: '20px',
                    marginBottom: '24px',
                    backgroundColor: '#f9fafb',
                }}>
                    {isRunning && (
                        <>
                            {queueStatus === 'queued' && (
                                <div style={{ marginBottom: '12px', padding: '10px 14px', backgroundColor: '#fef3c7', borderRadius: '6px', fontSize: '13px', color: '#92400e' }}>
                                    <strong>{position === 1 ? 'You are next in the queue.' : `Waiting in queue \u2014 ${position - 1} report${position - 1 === 1 ? '' : 's'} ahead of you.`}</strong>
                                    <div style={{ marginTop: '4px', color: '#a16207' }}>
                                        Other people are running reports too — yours will start automatically. No need to refresh.
                                    </div>
                                </div>
                            )}
                            <p style={{ fontSize: '14px', fontWeight: 500, color: '#1f2937', marginTop: 0, marginBottom: '12px' }}>
                                {queueStatus === 'queued'
                                    ? `Waiting to start: ${SUB_REPORTS[currentIndex].label} (${currentIndex + 1} of ${SUB_REPORTS.length})`
                                    : `Generating report ${currentIndex + 1} of ${SUB_REPORTS.length}: ${SUB_REPORTS[currentIndex].label}`}
                            </p>
                            <div style={{
                                width: '100%',
                                height: '8px',
                                backgroundColor: '#e5e7eb',
                                borderRadius: '4px',
                                overflow: 'hidden',
                                marginBottom: '16px',
                            }}>
                                <div style={{
                                    width: `${progressPercent}%`,
                                    height: '100%',
                                    backgroundColor: '#3b82f6',
                                    borderRadius: '4px',
                                    transition: 'width 0.3s ease',
                                }} />
                            </div>
                        </>
                    )}
                    <style>{`
                        @keyframes sla-spin {
                            to { transform: rotate(360deg); }
                        }
                    `}</style>
                    <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
                        {SUB_REPORTS.map((report, idx) => {
                            let status = 'pending';
                            if (errors[report.key]) status = 'error';
                            else if (completedKeys[report.key] && !errors[report.key]) status = 'done';
                            else if (isRunning && idx === currentIndex) status = 'loading';

                            const color = {
                                pending: '#9ca3af',
                                loading: '#3b82f6',
                                done: '#16a34a',
                                error: '#dc2626',
                            }[status];

                            const iconEl = status === 'loading' ? (
                                <span style={{
                                    display: 'inline-block',
                                    width: '14px',
                                    height: '14px',
                                    border: '2px solid #bfdbfe',
                                    borderTopColor: '#3b82f6',
                                    borderRadius: '50%',
                                    animation: 'sla-spin 0.7s linear infinite',
                                    flexShrink: 0,
                                }} />
                            ) : (
                                <span style={{ fontSize: '14px', width: '16px', textAlign: 'center', flexShrink: 0 }}>
                                    {{ pending: '\u25CB', done: '\u2713', error: '\u2717' }[status]}
                                </span>
                            );

                            return (
                                <li key={report.key} style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '8px',
                                    padding: '4px 0',
                                    fontSize: '13px',
                                    color,
                                    fontWeight: status === 'loading' ? 600 : 400,
                                }}>
                                    {iconEl}
                                    {report.label}
                                    {errors[report.key] && (
                                        <span style={{ fontSize: '12px', color: '#dc2626', marginLeft: '4px' }}>
                                            — {errors[report.key]}
                                        </span>
                                    )}
                                </li>
                            );
                        })}
                    </ul>
                </div>
            )}

            <label style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '13px', color: '#4b5563', cursor: 'pointer', marginBottom: '16px' }}>
                <input
                    type="checkbox"
                    checked={includeReasonSummary}
                    onChange={(e) => setIncludeReasonSummary(e.target.checked)}
                    style={{ width: '16px', height: '16px', cursor: 'pointer', accentColor: '#3b82f6' }}
                />
                Include override and pass summary
            </label>

            {/* Download button, shown once the report has finished with data */}
            {done && exportSections.length > 0 && (
                <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: '20px' }}>
                    <button
                        onClick={handleExport}
                        disabled={exporting}
                        style={{
                            padding: '10px 16px',
                            fontSize: '14px',
                            fontWeight: 500,
                            color: '#fff',
                            backgroundColor: exporting ? '#86c9a0' : '#16a34a',
                            border: 'none',
                            borderRadius: '6px',
                            cursor: exporting ? 'not-allowed' : 'pointer',
                            transition: 'all 0.2s ease',
                        }}
                    >
                        {exporting ? 'Preparing…' : 'Download Excel'}
                    </button>
                </div>
            )}

            {done && includeReasonSummary && (
                <SlaReasonSummaryPanel
                    heading="All SLA categories"
                    summary={mergeReasonSummaries(SUB_REPORTS.map((report) => {
                        const data = dataByKey[report.key];
                        if (!data || errors[report.key]) return null;
                        return reasonSummaryFromReports(data.reasonSummary, data.dailyReports);
                    }))}
                />
            )}

            {/* Stacked summary sections after done */}
            {done && SUB_REPORTS.map((report) => {
                const data = dataByKey[report.key];
                const hasError = !!errors[report.key];

                return (
                    <div key={report.key} style={{ marginBottom: '32px' }}>
                        <h3 style={{
                            fontSize: '15px',
                            fontWeight: 600,
                            color: '#1f2937',
                            marginBottom: '12px',
                            paddingBottom: '8px',
                            borderBottom: '2px solid #e5e7eb',
                        }}>
                            {report.label}
                        </h3>
                        {hasError ? (
                            <p style={{ fontSize: '13px', color: '#dc2626' }}>
                                Failed to load: {errors[report.key]}
                            </p>
                        ) : data ? (
                            <>
                                {renderSummary(report.key, data)}
                                {includeReasonSummary && (
                                    <SlaReasonSummaryPanel summary={reasonSummaryFromReports(data.reasonSummary, data.dailyReports)} />
                                )}
                            </>
                        ) : (
                            <p style={{ fontSize: '13px', color: '#6b7280' }}>No data available.</p>
                        )}
                    </div>
                );
            })}
        </div>
    );
};

export default SlaSummaryReport;
